import { useCallback, useEffect, useMemo } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  addEdge,
  Handle,
  Position,
  BackgroundVariant,
  MarkerType,
  BaseEdge,
  getBezierPath,
  EdgeLabelRenderer,
  type Node,
  type Edge,
  type NodeProps,
  type EdgeProps,
  type Connection,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import type { PlanStep, StepResult } from '@verifyloop/core';
import { formatDuration } from '../lib/format';
import { StepStatusPill, StepTypePill } from './Pills';

export interface N8nStepNodeData extends Record<string, unknown> {
  index: number;
  step: PlanStep;
  result?: StepResult;
  isRunning?: boolean;
  isSelected?: boolean;
  isFailing?: boolean;
  isBranch?: boolean;
  onSelect: (index: number) => void;
  onInsertNext?: (afterIndex: number) => void;
  onSimulateStep?: (index: number) => void;
}

const STEP_ICONS: Record<string, string> = {
  navigation: '🌐',
  action: '👆',
  assertion: '🎯',
  wait: '⏳',
  extract: '📥',
  setup: '⚙️',
  teardown: '🧹',
  dependency: '🔗',
};

// --------------------------------------------------------------------------------
// CUSTOM NODE: N8N Style Rich Step Card
// --------------------------------------------------------------------------------
export function N8nStepCardNode(props: NodeProps): JSX.Element {
  const data = props.data as unknown as N8nStepNodeData;
  const { index, step, result, isRunning, isSelected, isFailing, onSelect } = data;

  const icon = STEP_ICONS[step.type] ?? '⚡';
  const status = isRunning ? 'running' : result?.status ?? 'pending';
  const isHealed = Boolean(result?.healProposal || step.healed);

  const statusBorderColor = isFailing
    ? '#ef4444'
    : isHealed
      ? '#06b6d4'
      : status === 'passed'
        ? '#10b981'
        : status === 'running'
          ? '#3b82f6'
          : 'var(--border)';

  // Highlight mustache tokens in description (e.g. {{user.email}})
  const renderDescription = (text: string) => {
    const parts = text.split(/(\{\{[^}]+\}\})/g);
    return parts.map((part, i) => {
      if (part.startsWith('{{') && part.endsWith('}}')) {
        return (
          <span
            key={i}
            style={{
              background: 'rgba(56, 189, 248, 0.15)',
              color: '#38bdf8',
              borderRadius: 3,
              padding: '1px 4px',
              fontFamily: 'monospace',
              fontSize: '11px',
              fontWeight: 600,
            }}
          >
            {part}
          </span>
        );
      }
      return part;
    });
  };

  return (
    <div
      onClick={() => onSelect(index)}
      style={{
        width: 250,
        background: isSelected ? 'var(--bg-panel)' : 'var(--bg-inset)',
        border: `2px solid ${isSelected ? '#3b82f6' : statusBorderColor}`,
        borderRadius: 10,
        boxShadow: isSelected
          ? '0 0 0 3px rgba(59, 130, 246, 0.25), 0 8px 24px rgba(0, 0, 0, 0.15)'
          : isFailing
            ? '0 0 12px rgba(239, 68, 68, 0.25)'
            : '0 4px 14px rgba(0, 0, 0, 0.08)',
        transition: 'all 0.15s ease-out',
        cursor: 'pointer',
        position: 'relative',
        color: 'var(--text)',
        userSelect: 'none',
      }}
    >
      {/* Input Handle (Left) */}
      <Handle
        type="target"
        position={Position.Left}
        style={{
          background: '#64748b',
          width: 9,
          height: 9,
          border: '2px solid var(--border)',
          left: -6,
        }}
      />

      {/* Node Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 10px',
          background: isSelected ? 'rgba(59, 130, 246, 0.12)' : 'rgba(148, 163, 184, 0.08)',
          borderBottom: '1px solid var(--border)',
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 14 }}>{icon}</span>
          <span
            style={{
              fontSize: 10.5,
              fontWeight: 800,
              color: 'var(--text-muted)',
              background: 'rgba(148, 163, 184, 0.12)',
              padding: '1px 5px',
              borderRadius: 4,
            }}
          >
            #{index + 1}
          </span>
          <StepTypePill type={step.type} />
          {data.isBranch ? (
            <span
              style={{
                fontSize: 9.5,
                fontWeight: 700,
                color: '#f87171',
                background: 'rgba(239, 68, 68, 0.15)',
                padding: '1px 5px',
                borderRadius: 3,
                border: '1px solid rgba(239, 68, 68, 0.3)',
                textTransform: 'uppercase',
              }}
            >
              Branch
            </span>
          ) : null}
          {isHealed ? (
            <span
              style={{
                fontSize: 9.5,
                fontWeight: 800,
                color: '#06b6d4',
                background: 'rgba(6, 182, 212, 0.12)',
                padding: '1px 5px',
                borderRadius: 3,
                border: '1px solid rgba(6, 182, 212, 0.35)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 2,
              }}
              title="Locators were automatically repaired by Justloop Auto-Heal"
            >
              🪄 Healed
            </span>
          ) : null}
        </div>

        <div>
          {result ? (
            <StepStatusPill status={result.status} />
          ) : isRunning ? (
            <span
              style={{
                fontSize: 10,
                padding: '1px 6px',
                borderRadius: 9999,
                background: '#2563eb',
                color: '#fff',
                animation: 'pulse 1.5s infinite',
                fontWeight: 600,
              }}
            >
              RUNNING
            </span>
          ) : (
            <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>idle</span>
          )}
        </div>
      </div>

      {/* Node Body */}
      <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div
          style={{
            fontSize: 12.5,
            fontWeight: 500,
            lineHeight: 1.35,
            color: 'var(--text)',
            wordBreak: 'break-word',
          }}
        >
          {renderDescription(step.description)}
        </div>

        {isHealed ? (
          <div
            style={{
              fontSize: 10,
              fontFamily: 'monospace',
              color: '#06b6d4',
              background: 'rgba(6, 182, 212, 0.08)',
              border: '1px dashed rgba(6, 182, 212, 0.35)',
              padding: '2px 6px',
              borderRadius: 4,
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              overflow: 'hidden',
            }}
            title={`Auto-healed: ${result?.healProposal?.repairedTarget ?? step.healed?.repairedTarget ?? step.target}`}
          >
            <span>🪄</span>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {result?.healProposal?.repairedTarget ?? step.healed?.repairedTarget ?? step.target}
            </span>
          </div>
        ) : step.target ? (
          <div
            style={{
              fontSize: 11,
              fontFamily: 'monospace',
              color: 'var(--text-muted)',
              background: 'var(--bg-inset)',
              padding: '2px 6px',
              borderRadius: 4,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              marginTop: 2,
            }}
            title={step.target}
          >
            {renderDescription(step.target)}
          </div>
        ) : null}

        {result?.error ? (
          <div
            style={{
              fontSize: 11,
              color: '#f87171',
              background: 'rgba(239, 68, 68, 0.15)',
              padding: '4px 6px',
              borderRadius: 4,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={result.error}
          >
            ⚠️ {result.error}
          </div>
        ) : null}
      </div>

      {/* Node Footer */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '6px 10px',
          borderTop: '1px solid var(--border)',
          fontSize: 10.5,
          color: 'var(--text-muted)',
          background: 'rgba(148, 163, 184, 0.05)',
          borderBottomLeftRadius: 8,
          borderBottomRightRadius: 8,
        }}
      >
        <span>
          {result?.durationMs !== undefined ? (
            formatDuration(result.durationMs)
          ) : step.durationMs !== undefined ? (
            `~${formatDuration(step.durationMs)}`
          ) : (
            '—'
          )}
        </span>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {result && result.artifacts && result.artifacts.length > 0 ? (
            <span style={{ color: '#38bdf8', display: 'flex', alignItems: 'center', gap: 3 }}>
              📸 {result.artifacts.length}
            </span>
          ) : null}

          {data.onSimulateStep ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                data.onSimulateStep?.(index);
              }}
              title="Simulate / Run to this step"
              style={{
                background: 'rgba(59, 130, 246, 0.2)',
                color: '#60a5fa',
                border: '1px solid rgba(59, 130, 246, 0.4)',
                borderRadius: 4,
                padding: '1px 6px',
                fontSize: 10,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              ▶ Run
            </button>
          ) : null}
        </div>
      </div>

      {/* Output Handle (Right) */}
      <Handle
        type="source"
        position={Position.Right}
        style={{
          background: '#64748b',
          width: 9,
          height: 9,
          border: '2px solid var(--border)',
          right: -6,
        }}
      />
    </div>
  );
}

// --------------------------------------------------------------------------------
// CUSTOM EDGE: Bezier curve with middle (+) button
// --------------------------------------------------------------------------------
export function N8nBezierEdge(props: EdgeProps): JSX.Element {
  const {
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    style = {},
    markerEnd,
    data,
  } = props;

  const edgeData = data as unknown as
    | { afterIndex?: number; onInsert?: (afterIndex: number) => void }
    | undefined;

  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  return (
    <>
      <BaseEdge id={id} path={edgePath} style={style} markerEnd={markerEnd} />
      {edgeData?.onInsert !== undefined && edgeData.afterIndex !== undefined ? (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
              pointerEvents: 'all',
            }}
            className="nodrag nopan"
          >
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                edgeData.onInsert?.(edgeData.afterIndex!);
              }}
              title="Insert step here (+)"
              style={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                background: 'var(--bg-panel)',
                color: 'var(--text-muted)',
                border: '1.5px solid var(--border)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 13,
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#3b82f6';
                e.currentTarget.style.color = '#ffffff';
                e.currentTarget.style.borderColor = '#60a5fa';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'var(--bg-panel)';
                e.currentTarget.style.color = 'var(--text-muted)';
                e.currentTarget.style.borderColor = 'var(--border)';
              }}
            >
              +
            </button>
          </div>
        </EdgeLabelRenderer>
      ) : null}
    </>
  );
}

// --------------------------------------------------------------------------------
// CANVAS WORKFLOW COMPONENT
// --------------------------------------------------------------------------------
const nodeTypes = {
  stepCard: N8nStepCardNode,
};

const edgeTypes = {
  n8nBezier: N8nBezierEdge,
};

export interface N8nPlaybookCanvasProps {
  steps: readonly PlanStep[];
  stepResults?: readonly StepResult[];
  selectedStepIndex: number | null;
  onSelectStep: (index: number) => void;
  activeRunningIndex?: number | null;
  onInsertStep?: (afterIndex: number) => void;
  onSimulateStep?: (index: number) => void;
  onAddBranch?: () => void;
  onExportPlaywright?: () => void;
  height?: number | string;
}

export function N8nPlaybookCanvas({
  steps,
  stepResults,
  selectedStepIndex,
  onSelectStep,
  activeRunningIndex,
  onInsertStep,
  onSimulateStep,
  onAddBranch,
  onExportPlaywright,
  height = 520,
}: N8nPlaybookCanvasProps): JSX.Element {
  // Convert steps to ReactFlow nodes
  const initialNodes: Node[] = useMemo(() => {
    let mainX = 50;
    return steps.map((step, index) => {
      const result = stepResults?.[index];
      const isSelected = selectedStepIndex === index;
      const isRunning = activeRunningIndex === index;
      const isFailing = result?.status === 'failed';

      const isBranch =
        Boolean(step.optional) ||
        /\b(error|fail|invalid|reject|unauthorized|forbidden|negative|alert)\b/i.test(
          step.description + ' ' + (step.target ?? ''),
        );

      let x: number;
      let y: number;

      if (isBranch && index >= 2) {
        // Layout on lower alternative branch track (y = 290)
        x = Math.max(mainX - 160, 50 + (index - 1) * 310);
        y = 290;
      } else {
        // Main sequence track (y = 110)
        x = 50 + index * 310;
        y = 110;
        mainX = x;
      }

      return {
        id: `step-${index}`,
        type: 'stepCard',
        position: { x, y },
        data: {
          index,
          step,
          result,
          isRunning,
          isSelected,
          isFailing,
          isBranch,
          onSelect: onSelectStep,
          onInsertNext: onInsertStep,
          onSimulateStep,
        },
      };
    });
  }, [steps, stepResults, selectedStepIndex, activeRunningIndex, onSelectStep, onInsertStep, onSimulateStep]);

  // Convert step transitions to ReactFlow edges
  const initialEdges: Edge[] = useMemo(() => {
    const edges: Edge[] = [];
    for (let i = 0; i < steps.length - 1; i++) {
      const isPassed = stepResults?.[i]?.status === 'passed';
      const isRunning = activeRunningIndex === i;
      const targetStep = steps[i + 1];

      const isBranchEdge =
        Boolean(targetStep?.optional) ||
        /\b(error|fail|invalid|reject|unauthorized|forbidden|negative|alert)\b/i.test(
          targetStep?.description ?? '',
        );

      edges.push({
        id: `edge-${i}-${i + 1}`,
        source: `step-${i}`,
        target: `step-${i + 1}`,
        type: 'n8nBezier',
        animated: isRunning,
        style: {
          stroke: isBranchEdge ? '#f87171' : isPassed ? '#10b981' : isRunning ? '#3b82f6' : 'var(--border-strong)',
          strokeWidth: isBranchEdge ? 2.5 : 2,
          strokeDasharray: isBranchEdge ? '4 3' : undefined,
        },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: isBranchEdge ? '#f87171' : isPassed ? '#10b981' : isRunning ? '#3b82f6' : 'var(--border-strong)',
          width: 14,
          height: 14,
        },
        data: {
          afterIndex: i,
          onInsert: onInsertStep,
        },
      });
    }
    return edges;
  }, [steps.length, stepResults, activeRunningIndex, onInsertStep]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Synchronize state when steps or selection change
  useEffect(() => {
    setNodes(initialNodes);
  }, [initialNodes, setNodes]);

  useEffect(() => {
    setEdges(initialEdges);
  }, [initialEdges, setEdges]);

  const onConnect = useCallback(
    (params: Connection) => setEdges((eds) => addEdge(params, eds)),
    [setEdges],
  );

  return (
    <div
      className="n8n-playbook-canvas"
      style={{
        width: '100%',
        height,
        background: 'var(--bg-inset)',
        border: '1px solid var(--border)',
        borderRadius: 10,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Canvas Floating Quick Toolbar */}
      <div
        style={{
          position: 'absolute',
          top: 12,
          left: 12,
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          background: 'var(--bg-panel)',
          backdropFilter: 'blur(8px)',
          border: '1px solid var(--border)',
          borderRadius: 8,
          padding: '4px 10px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
        }}
      >
        <span style={{ fontSize: 11, fontWeight: 800, color: 'var(--text)', letterSpacing: '0.04em', display: 'flex', alignItems: 'center', gap: 5 }}>
          <span>⚡</span>
          <span>PLAYBOOK STUDIO</span>
        </span>
        <div style={{ height: 14, width: 1, background: 'var(--border)' }} />
        {onInsertStep ? (
          <button
            type="button"
            className="btn btn--small btn--ghost"
            onClick={() => onInsertStep(steps.length - 1)}
            style={{ fontSize: 11, padding: '2px 8px' }}
          >
            + Add Step
          </button>
        ) : null}
        {onAddBranch ? (
          <button
            type="button"
            className="btn btn--small btn--ghost"
            onClick={onAddBranch}
            style={{ fontSize: 11, padding: '2px 8px', color: '#f87171' }}
            title="Add negative validation assertion branch"
          >
            🌿 Add Negative Branch
          </button>
        ) : null}
        {onExportPlaywright ? (
          <button
            type="button"
            className="btn btn--small btn--ghost"
            onClick={onExportPlaywright}
            style={{ fontSize: 11, padding: '2px 8px' }}
            title="Export as standalone Playwright (.spec.ts) file"
          >
            📥 Export .spec.ts
          </button>
        ) : null}
      </div>

      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView
        fitViewOptions={{ padding: 0.25 }}
        minZoom={0.2}
        maxZoom={1.8}
        proOptions={{ hideAttribution: true }}
      >
        <Background variant={BackgroundVariant.Dots} gap={18} size={1.2} color="var(--border-strong)" />
        <Controls
          position="bottom-left"
          style={{
            background: 'var(--bg-panel)',
            border: '1px solid var(--border)',
            borderRadius: 6,
            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
          }}
        />
        <MiniMap
          nodeColor={(n) => {
            const data = n.data as unknown as N8nStepNodeData | undefined;
            if (data?.result?.status === 'passed') return '#10b981';
            if (data?.result?.status === 'failed') return '#ef4444';
            if (data?.isRunning) return '#3b82f6';
            return '#64748b';
          }}
          maskColor="rgba(0, 0, 0, 0.08)"
          position="bottom-right"
          style={{
            background: 'var(--bg-panel)',
            border: '1px solid var(--border)',
            borderRadius: 6,
            height: 90,
            width: 130,
          }}
        />
      </ReactFlow>
    </div>
  );
}

export const PlaybookCanvas = N8nPlaybookCanvas;
export const PlaybookStudio = N8nPlaybookCanvas;
